import React, { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import Breadcrums from "../../common/breadcrumbs";
import {
  ChatbotRestaurantDetails,
  ChatbotRestaurantList,
  UpdateChatbotAccess,
} from "../../services/restaurant.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import ChatbotManagementStats from "./components/ChatbotManagementStats";
import ChatbotRestaurantDetailsModal from "./components/ChatbotRestaurantDetailsModal";
import ChatbotRestaurantTable from "./components/ChatbotRestaurantTable";
import ReferenceGallery from "./components/ReferenceGallery";
import type {
  CostPeriod,
  RestaurantDetails,
  RestaurantListPayload,
  RestaurantRow,
  ServiceResponse,
  SortState,
  SummaryData,
} from "./chatbotManagement.types";
import "./chatbot-management.css";

const ChatbotManagement = (): React.ReactElement => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<RestaurantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [accessFilter, setAccessFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [details, setDetails] = useState<RestaurantDetails | null>(null);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [costPeriod, setCostPeriod] = useState<CostPeriod>("weekly");
  const [sorting, setSorting] = useState<SortState>({ column: "tokens_used", order: -1 });
  const [defaultSorting, setDefaultSorting] = useState(true);
  const hasLoaded = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    if (!hasLoaded.current) setLoading(true);
    try {
      const res = await ChatbotRestaurantList({
        page,
        per_page: 10,
        search: search || undefined,
        access_enabled: accessFilter || undefined,
        sort_by: sorting.column,
        sort_direction: sorting.order === 1 ? "asc" : "desc",
      }) as ServiceResponse<RestaurantListPayload>;

      if (!res?.success) throw new Error(res?.message || t("admin_chatbot_load_error"));
      setRows(res?.data?.list || []);
      setTotal(res?.data?.total_records || 0);
      setSummary(res?.data?.summary || null);
    } catch {
      void Swal.fire({ icon: "error", text: t("admin_chatbot_load_error"), ...SWAL_SETTINGS } as any);
    } finally {
      hasLoaded.current = true;
      setLoading(false);
    }
  }, [page, search, accessFilter, sorting, t]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [load]);

  const toggleAccess = async (row: RestaurantRow): Promise<void> => {
    const nextEnabled = !row.access_enabled;
    const confirmation = await Swal.fire({
      icon: "warning",
      title: t(nextEnabled ? "admin_chatbot_enable_title" : "admin_chatbot_disable_title"),
      text: t(nextEnabled ? "admin_chatbot_enable_confirm" : "admin_chatbot_disable_confirm", {
        restaurant: row.restaurant_name,
      }),
      showCancelButton: true,
      confirmButtonText: t(nextEnabled ? "admin_chatbot_enable" : "admin_chatbot_disable"),
    });
    if (!confirmation.isConfirmed) return;

    setUpdatingId(row.restaurant_id);
    setRows((current) =>
      current.map((item) =>
        item.restaurant_id === row.restaurant_id ? { ...item, access_enabled: nextEnabled } : item
      )
    );

    try {
      const res = await UpdateChatbotAccess(row.restaurant_id, nextEnabled) as ServiceResponse<unknown>;
      if (!res?.success) throw new Error(res?.message || t("admin_chatbot_update_error"));
      await Swal.fire({
        icon: "success",
        text: t(nextEnabled ? "admin_chatbot_enabled_success" : "admin_chatbot_disabled_success"),
        ...SWAL_SETTINGS,
      } as any);
      await load();
    } catch {
      setRows((current) =>
        current.map((item) =>
          item.restaurant_id === row.restaurant_id
            ? { ...item, access_enabled: row.access_enabled }
            : item
        )
      );
      void Swal.fire({ icon: "error", text: t("admin_chatbot_update_error"), ...SWAL_SETTINGS } as any);
    } finally {
      setUpdatingId(null);
    }
  };

  const openDetails = async (row: RestaurantRow): Promise<void> => {
    try {
      const res = await ChatbotRestaurantDetails(row.restaurant_id) as ServiceResponse<RestaurantDetails>;
      if (!res?.success) throw new Error(res?.message || t("admin_chatbot_details_error"));
      setDetails(res?.data || row);
    } catch {
      void Swal.fire({ icon: "error", text: t("admin_chatbot_details_error"), ...SWAL_SETTINGS } as any);
    }
  };

  const handleSort = (_event: unknown, column: string): void => {
    setDefaultSorting(false);
    setPage(1);
    setSorting((current) => ({
      column,
      order: current.column === column && current.order === 1 ? -1 : 1,
    }));
  };

  const periodTokens = summary?.[`${costPeriod}_tokens_used`] || 0;
  const periodCost = summary?.[`${costPeriod}_cost_usd`] || 0;
  const costEntries = summary?.cost_by_restaurant?.[costPeriod] || [];

  return (
    <>
      <Breadcrums
        data={[
          { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
          { title: t("admin_chatbot_title"), url: "" },
        ]}
      />

      <div className="cbm-page">
        <div className="cbm-header">
          <h1>{t("admin_chatbot_title")}</h1>
          <p>{t("admin_chatbot_description")}</p>
        </div>

        <div className="cbm-mid">
          <div className="cbm-panel">
            <ReferenceGallery inline />
          </div>
          <ChatbotManagementStats
            summary={summary}
            costPeriod={costPeriod}
            setCostPeriod={setCostPeriod}
            periodTokens={periodTokens}
            periodCost={periodCost}
            costEntries={costEntries}
            t={t}
          />
        </div>

        <ChatbotRestaurantTable
          rows={rows}
          loading={loading}
          search={search}
          accessFilter={accessFilter}
          page={page}
          total={total}
          sorting={sorting}
          defaultSorting={defaultSorting}
          updatingId={updatingId}
          t={t}
          onSearchChange={(value) => {
            setPage(1);
            setSearch(value);
          }}
          onAccessFilterChange={(value) => {
            setPage(1);
            setAccessFilter(value);
          }}
          onSort={handleSort}
          onToggleAccess={(row) => {
            void toggleAccess(row);
          }}
          onOpenDetails={(row) => {
            void openDetails(row);
          }}
          onPageChange={setPage}
        />
      </div>

      <ChatbotRestaurantDetailsModal details={details} onClose={() => setDetails(null)} t={t} />
    </>
  );
};

export default ChatbotManagement;
